// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { StepComponentProps } from '../../types/wizard';
import { FileUploadField } from '../shared/FileUploadField';
import { toIsoDate } from '../../utils/date';

const YES_NO_OPTIONS = [
  { value: 'Yes', text: 'Yes' },
  { value: 'No', text: 'No' },
];

const PROVIDER_OPTIONS = [
  { value: 'Irish Life', text: 'Irish Life' },
  { value: 'Zurich', text: 'Zurich' },
  { value: 'Aviva', text: 'Aviva' },
  { value: 'New Ireland', text: 'New Ireland' },
  { value: 'Standard Life', text: 'Standard Life' },
  { value: 'Royal London', text: 'Royal London' },
  { value: 'Canada Life', text: 'Canada Life' },
  { value: 'Other', text: 'Other' },
];

const PENSION_TYPE_OPTIONS = [
  { value: 'PRB_BOB', text: 'PRB / Buy-out Bond' },
  { value: 'PRSA_STANDARD', text: 'PRSA (Standard)' },
  { value: 'PRSA_NON_STANDARD', text: 'PRSA (Non-Standard)' },
  { value: 'PRSA_AVC', text: 'PRSA AVC' },
  { value: 'PERSONAL_PENSION', text: 'Personal Pension' },
  { value: 'DEFINED_CONTRIBUTION', text: 'Defined Contribution' },
  { value: 'EXECUTIVE_PENSION', text: 'Executive Pension' },
  { value: 'MASTERTRUST', text: 'Mastertrust' },
  { value: 'DIRECTORS_PENSION', text: "Director's Pension" },
  { value: 'ARF', text: 'ARF (Approved Retirement Fund)' },
  { value: 'VESTED_PRSA', text: 'Vested PRSA' },
];

const TRANSFER_TYPE_OPTIONS = [
  { value: 'Cash', text: 'Cash' },
  { value: 'In-specie', text: 'In-specie' },
];

const COUNTRY_OPTIONS = [
  { value: 'AF', text: 'Afghanistan' },
  { value: 'AL', text: 'Albania' },
  { value: 'DZ', text: 'Algeria' },
  { value: 'AR', text: 'Argentina' },
  { value: 'AU', text: 'Australia' },
  { value: 'AT', text: 'Austria' },
  { value: 'BE', text: 'Belgium' },
  { value: 'BR', text: 'Brazil' },
  { value: 'BG', text: 'Bulgaria' },
  { value: 'CA', text: 'Canada' },
  { value: 'CL', text: 'Chile' },
  { value: 'CN', text: 'China' },
  { value: 'CO', text: 'Colombia' },
  { value: 'HR', text: 'Croatia' },
  { value: 'CY', text: 'Cyprus' },
  { value: 'CZ', text: 'Czech Republic' },
  { value: 'DK', text: 'Denmark' },
  { value: 'EG', text: 'Egypt' },
  { value: 'EE', text: 'Estonia' },
  { value: 'FI', text: 'Finland' },
  { value: 'FR', text: 'France' },
  { value: 'DE', text: 'Germany' },
  { value: 'GR', text: 'Greece' },
  { value: 'HK', text: 'Hong Kong' },
  { value: 'HU', text: 'Hungary' },
  { value: 'IS', text: 'Iceland' },
  { value: 'IN', text: 'India' },
  { value: 'ID', text: 'Indonesia' },
  { value: 'IE', text: 'Ireland' },
  { value: 'IL', text: 'Israel' },
  { value: 'IT', text: 'Italy' },
  { value: 'JP', text: 'Japan' },
  { value: 'LV', text: 'Latvia' },
  { value: 'LT', text: 'Lithuania' },
  { value: 'LU', text: 'Luxembourg' },
  { value: 'MY', text: 'Malaysia' },
  { value: 'MT', text: 'Malta' },
  { value: 'MX', text: 'Mexico' },
  { value: 'NL', text: 'Netherlands' },
  { value: 'NZ', text: 'New Zealand' },
  { value: 'NO', text: 'Norway' },
  { value: 'PK', text: 'Pakistan' },
  { value: 'PH', text: 'Philippines' },
  { value: 'PL', text: 'Poland' },
  { value: 'PT', text: 'Portugal' },
  { value: 'RO', text: 'Romania' },
  { value: 'RU', text: 'Russia' },
  { value: 'SA', text: 'Saudi Arabia' },
  { value: 'SG', text: 'Singapore' },
  { value: 'SK', text: 'Slovakia' },
  { value: 'SI', text: 'Slovenia' },
  { value: 'ZA', text: 'South Africa' },
  { value: 'KR', text: 'South Korea' },
  { value: 'ES', text: 'Spain' },
  { value: 'SE', text: 'Sweden' },
  { value: 'CH', text: 'Switzerland' },
  { value: 'TW', text: 'Taiwan' },
  { value: 'TH', text: 'Thailand' },
  { value: 'TR', text: 'Turkey' },
  { value: 'AE', text: 'United Arab Emirates' },
  { value: 'GB', text: 'United Kingdom' },
  { value: 'US', text: 'United States' },
];

/** Strip everything except digits and dot, keep only first dot */
function toRawNumber(display: string): string {
  const stripped = display.replace(/[^0-9.]/g, '');
  const parts = stripped.split('.');
  if (parts.length <= 1) return stripped;
  return parts[0] + '.' + parts.slice(1).join('');
}

/** Format a raw numeric string as 1,234.56 for display (without € symbol) */
function formatEuro(raw: string): string {
  if (!raw) return '';
  const parts = raw.split('.');
  const intPart = parts[0].replace(/^0+(?=\d)/, '');
  const formatted = (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const dec = parts.length > 1 ? '.' + parts[1] : '';
  return `${formatted}${dec}`;
}

/** On blur, ensure 2 decimal places */
function formatEuroBlur(raw: string): string {
  if (!raw) return '';
  const num = parseFloat(raw);
  if (isNaN(num)) return '';
  return num.toFixed(2);
}

// Select chevron SVG
const chevronIcon = (
  <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1.5L6 6.5L11 1.5" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Calendar icon SVG
const calendarIcon = (maskId: string) => (
  <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id={maskId} style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
      <path d="M16 0H0V16H16V0Z" fill="white"/>
    </mask>
    <g mask={`url(#${maskId})`}>
      <path d="M5.3335 3.83333C5.06016 3.83333 4.8335 3.60666 4.8335 3.33333V1.33333C4.8335 1.05999 5.06016 0.833328 5.3335 0.833328C5.60683 0.833328 5.8335 1.05999 5.8335 1.33333V3.33333C5.8335 3.60666 5.60683 3.83333 5.3335 3.83333Z" fill="#141414"/>
      <path d="M10.6665 3.83333C10.3932 3.83333 10.1665 3.60666 10.1665 3.33333V1.33333C10.1665 1.05999 10.3932 0.833328 10.6665 0.833328C10.9398 0.833328 11.1665 1.05999 11.1665 1.33333V3.33333C11.1665 3.60666 10.9398 3.83333 10.6665 3.83333Z" fill="#141414"/>
      <path d="M13.6668 6.55989H2.3335C2.06016 6.55989 1.8335 6.33322 1.8335 6.05989C1.8335 5.78656 2.06016 5.55989 2.3335 5.55989H13.6668C13.9402 5.55989 14.1668 5.78656 14.1668 6.05989C14.1668 6.33322 13.9402 6.55989 13.6668 6.55989Z" fill="#141414"/>
      <path d="M10.6667 15.1667H5.33333C2.9 15.1667 1.5 13.7667 1.5 11.3333V5.66666C1.5 3.23333 2.9 1.83333 5.33333 1.83333H10.6667C13.1 1.83333 14.5 3.23333 14.5 5.66666V11.3333C14.5 13.7667 13.1 15.1667 10.6667 15.1667ZM5.33333 2.83333C3.42667 2.83333 2.5 3.75999 2.5 5.66666V11.3333C2.5 13.24 3.42667 14.1667 5.33333 14.1667H10.6667C12.5733 14.1667 13.5 13.24 13.5 11.3333V5.66666C13.5 3.75999 12.5733 2.83333 10.6667 2.83333H5.33333Z" fill="#141414"/>
    </g>
  </svg>
);

export function StepTransfersIn({
  data,
  onFieldChange,
  pendingFiles,
  onAddFile,
  onRemoveFile,
}: StepComponentProps) {
  const hasTransfers = (data.hasReceivedTransfers as string) === 'Yes';
  const sameEmployment = (data.sameEmployment as string);
  const transferFromOverseas = (data.transferFromOverseas as string) === 'Yes';
  const wasTaxPaid = (data.wasTaxPaid as string) === 'Yes';

  const handleCurrencyChange = (field: string, inputValue: string) => {
    const raw = toRawNumber(inputValue);
    if (raw && !/^\d*\.?\d*$/.test(raw)) return;
    onFieldChange(field, raw);
  };

  const handleCurrencyBlur = (field: string) => {
    const raw = (data[field] as string) || '';
    if (raw) onFieldChange(field, formatEuroBlur(raw));
  };

  const getCurrencyDisplay = (field: string): string => {
    const raw = (data[field] as string) || '';
    return raw ? formatEuro(raw) : '';
  };

  const inputClass =
    "w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary";

  const selectClass = (hasValue: boolean) =>
    `w-full h-[48px] px-md pr-xxl rounded-lg border border-grey-04 bg-white ${hasValue ? "text-[#333]" : "text-[#B6B6B6]"} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer`;

  const dateInputClass = (hasValue: boolean) =>
    `w-full h-[48px] px-md pr-2xl rounded-lg border border-grey-04 bg-white ${hasValue ? 'text-[#333]' : 'text-grey-04'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`;

  const currencyWrapper =
    "flex items-center w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white focus-within:border-purple-primary";

  const labelClass = "text-black font-sans text-body-02 font-medium leading-normal not-italic";

  return (
    <div className="flex flex-col gap-lg">
      {/* Title */}
      <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Transfer In
      </div>

      {/* Has the policy received any transfers? */}
      <div className="flex flex-col gap-sm">
        <div className={labelClass}>
          Has the policy received any transfers from other schemes or arrangements? <span className="text-[#E7000B]">*</span>
        </div>
        <div className="relative">
          <select
            value={(data.hasReceivedTransfers as string) || ''}
            onChange={(e) => onFieldChange('hasReceivedTransfers', e.target.value)}
            className={selectClass(!!(data.hasReceivedTransfers as string))}
          >
            <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
            {YES_NO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
            ))}
          </select>
          {chevronIcon}
        </div>
      </div>

      {hasTransfers && (
        <>
          {/* Amount Transferred in | Policy/Account Provider */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className={labelClass}>
                Amount Transferred in <span className="text-[#E7000B]">*</span>
              </div>
              <div className={currencyWrapper}>
                <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={getCurrencyDisplay('detailAmountTransferred')}
                  onChange={(e) => handleCurrencyChange('detailAmountTransferred', e.target.value)}
                  onBlur={() => handleCurrencyBlur('detailAmountTransferred')}
                  className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                />
              </div>
            </div>
            <div className="flex flex-col gap-sm flex-1">
              <div className={labelClass}>
                Policy/ Account Provider <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative">
                <select
                  value={(data.detailProvider as string) || ''}
                  onChange={(e) => onFieldChange('detailProvider', e.target.value)}
                  className={selectClass(!!(data.detailProvider as string))}
                >
                  <option value="" disabled className="text-[#B6B6B6]">Choose a provider</option>
                  {PROVIDER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                  ))}
                </select>
                {chevronIcon}
              </div>
            </div>
          </div>

          {/* Date Transferred in | Policy/Account/Pension Type */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className={labelClass}>
                Date Transferred in <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative">
                <input
                  type="date"
                  value={(data.detailDateTransferred as string) || ''}
                  onChange={(e) => onFieldChange('detailDateTransferred', toIsoDate(e.target.value))}
                  className={dateInputClass(!!(data.detailDateTransferred as string))}
                />
                {calendarIcon('mask_detail_date_transferred')}
              </div>
            </div>
            <div className="flex flex-col gap-sm flex-1">
              <div className={labelClass}>
                Policy/Account/Pension Type <span className="text-[#E7000B]">*</span>
              </div>
              <div className="relative">
                <select
                  value={(data.detailPolicyType as string) || ''}
                  onChange={(e) => onFieldChange('detailPolicyType', e.target.value)}
                  className={selectClass(!!(data.detailPolicyType as string))}
                >
                  <option value="" disabled className="text-[#B6B6B6]">Choose a type</option>
                  {PENSION_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                  ))}
                </select>
                {chevronIcon}
              </div>
            </div>
          </div>

          {/* Policy/Account Reference Number | Name of Scheme transfer received from */}
          <div className="flex flex-row gap-lg">
            <div className="flex flex-col gap-sm flex-1">
              <div className={labelClass}>
                Policy/Account Reference Number <span className="text-[#E7000B]">*</span>
              </div>
              <input
                type="text"
                value={(data.detailReferenceNumber as string) || ''}
                onChange={(e) => onFieldChange('detailReferenceNumber', e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="flex flex-col gap-sm flex-1">
              <div className={labelClass}>
                Name of Scheme transfer received from <span className="text-[#E7000B]">*</span>
              </div>
              <input
                type="text"
                value={(data.detailSchemeName as string) || ''}
                onChange={(e) => onFieldChange('detailSchemeName', e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Divider */}
          <div className="w-[888px] h-[1px] bg-grey-04" />

          {/* Was the transfer in relating to the same employment */}
          <div className="flex flex-col gap-sm">
            <div className={labelClass}>
              Was the transfer in relating to the same employment <span className="text-[#E7000B]">*</span>
            </div>
            <div className="relative">
              <select
                value={sameEmployment || ''}
                onChange={(e) => onFieldChange('sameEmployment', e.target.value)}
                className={selectClass(!!sameEmployment)}
              >
                <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
                {YES_NO_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                ))}
              </select>
              {chevronIcon}
            </div>
          </div>

          {/* If same employment = Yes */}
          {sameEmployment === 'Yes' && (
            <>
              {/* Date transferred in | Amount transferred in */}
              <div className="flex flex-row gap-lg">
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Date transferred in <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className="relative">
                    <input
                      type="date"
                      value={(data.sameEmpDateTransferred as string) || ''}
                      onChange={(e) => onFieldChange('sameEmpDateTransferred', toIsoDate(e.target.value))}
                      className={dateInputClass(!!(data.sameEmpDateTransferred as string))}
                    />
                    {calendarIcon('mask_same_emp_date')}
                  </div>
                </div>
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Amount transferred in <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className={currencyWrapper}>
                    <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={getCurrencyDisplay('sameEmpAmountTransferred')}
                      onChange={(e) => handleCurrencyChange('sameEmpAmountTransferred', e.target.value)}
                      onBlur={() => handleCurrencyBlur('sameEmpAmountTransferred')}
                      className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Employee Contributions Transferred Over | Employer contributions Transferred Over */}
              <div className="flex flex-row gap-lg">
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Employee Contributions Transferred Over <span className="text-[#E7000B]">*</span>
                  </div>
                  <input
                    type="text"
                    value={(data.employeeContributionsTransferred as string) || ''}
                    onChange={(e) => onFieldChange('employeeContributionsTransferred', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Employer contributions Transferred Over <span className="text-[#E7000B]">*</span>
                  </div>
                  <input
                    type="text"
                    value={(data.employerContributionsTransferred as string) || ''}
                    onChange={(e) => onFieldChange('employerContributionsTransferred', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Previous providers name | Previous policy number */}
              <div className="flex flex-row gap-lg">
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Previous providers name <span className="text-[#E7000B]">*</span>
                  </div>
                  <input
                    type="text"
                    value={(data.previousProviderName as string) || ''}
                    onChange={(e) => onFieldChange('previousProviderName', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Previous policy number <span className="text-[#E7000B]">*</span>
                  </div>
                  <input
                    type="text"
                    value={(data.previousPolicyNumber as string) || ''}
                    onChange={(e) => onFieldChange('previousPolicyNumber', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Type of Transfer */}
              <div className="flex flex-col gap-sm">
                <div className={labelClass}>
                  Type of Transfer <span className="text-[#E7000B]">*</span>
                </div>
                <div className="relative">
                  <select
                    value={(data.transferType as string) || ''}
                    onChange={(e) => onFieldChange('transferType', e.target.value)}
                    className={selectClass(!!(data.transferType as string))}
                  >
                    <option value="" disabled className="text-[#B6B6B6]">Select a option</option>
                    {TRANSFER_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                    ))}
                  </select>
                  {chevronIcon}
                </div>
              </div>

            </>
          )}

          {/* If same employment = No */}
          {sameEmployment === 'No' && (
            <>
              {/* Previous providers name | Previous policy number */}
              <div className="flex flex-row gap-lg">
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Previous providers name <span className="text-[#E7000B]">*</span>
                  </div>
                  <input
                    type="text"
                    value={(data.previousProvidersName as string) || ''}
                    onChange={(e) => onFieldChange('previousProvidersName', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Previous policy number <span className="text-[#E7000B]">*</span>
                  </div>
                  <input
                    type="text"
                    value={(data.previousPolicyNumberNo as string) || ''}
                    onChange={(e) => onFieldChange('previousPolicyNumberNo', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Employer details | Date Joined service */}
              <div className="flex flex-row gap-lg">
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Employer details <span className="text-[#E7000B]">*</span>
                  </div>
                  <input
                    type="text"
                    value={(data.employerDetails as string) || ''}
                    onChange={(e) => onFieldChange('employerDetails', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Date Joined service <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className="relative">
                    <input
                      type="date"
                      value={(data.transferDateJoinedService as string) || ''}
                      onChange={(e) => onFieldChange('transferDateJoinedService', toIsoDate(e.target.value))}
                      className={dateInputClass(!!(data.transferDateJoinedService as string))}
                    />
                    {calendarIcon('mask_date_joined_service')}
                  </div>
                </div>
              </div>

              {/* Date Joined Scheme | Date of Leaving Service */}
              <div className="flex flex-row gap-lg">
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Date Joined Scheme <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className="relative">
                    <input
                      type="date"
                      value={(data.transferDateJoinedScheme as string) || ''}
                      onChange={(e) => onFieldChange('transferDateJoinedScheme', toIsoDate(e.target.value))}
                      className={dateInputClass(!!(data.transferDateJoinedScheme as string))}
                    />
                    {calendarIcon('mask_date_joined_scheme')}
                  </div>
                </div>
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Date of Leaving Service <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className="relative">
                    <input
                      type="date"
                      value={(data.transferDateOfLeavingService as string) || ''}
                      onChange={(e) => onFieldChange('transferDateOfLeavingService', toIsoDate(e.target.value))}
                      className={dateInputClass(!!(data.transferDateOfLeavingService as string))}
                    />
                    {calendarIcon('mask_date_leaving_service')}
                  </div>
                </div>
              </div>

              {/* Salary as at leaving service | Total Employer contributions */}
              <div className="flex flex-row gap-lg">
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Salary as at leaving service <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className={currencyWrapper}>
                    <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={getCurrencyDisplay('salaryAtLeavingService')}
                      onChange={(e) => handleCurrencyChange('salaryAtLeavingService', e.target.value)}
                      onBlur={() => handleCurrencyBlur('salaryAtLeavingService')}
                      className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Total Employer contributions <span className="text-[#E7000B]">*</span>
                  </div>
                  <input
                    type="text"
                    value={(data.totalEmployerContributions as string) || ''}
                    onChange={(e) => onFieldChange('totalEmployerContributions', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Total Employee Contributions | Did the transfer come from overseas? */}
              <div className="flex flex-row gap-lg">
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Total Employee Contributions <span className="text-[#E7000B]">*</span>
                  </div>
                  <input
                    type="text"
                    value={(data.totalEmployeeContributions as string) || ''}
                    onChange={(e) => onFieldChange('totalEmployeeContributions', e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-sm flex-1">
                  <div className={labelClass}>
                    Did the transfer come from overseas? <span className="text-[#E7000B]">*</span>
                  </div>
                  <div className="relative">
                    <select
                      value={(data.transferFromOverseas as string) || ''}
                      onChange={(e) => onFieldChange('transferFromOverseas', e.target.value)}
                      className={selectClass(!!(data.transferFromOverseas as string))}
                    >
                      <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
                      {YES_NO_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                      ))}
                    </select>
                    {chevronIcon}
                  </div>
                </div>
              </div>

              {/* If transferFromOverseas = Yes */}
              {transferFromOverseas && (
                <>
                  {/* What country did the overseas pension come from? | Was there tax paid on transfer out? */}
                  <div className="flex flex-row gap-lg">
                    <div className="flex flex-col gap-sm flex-1">
                      <div className={labelClass}>
                        What country did the overseas pension come from? <span className="text-[#E7000B]">*</span>
                      </div>
                      <div className="relative">
                        <select
                          value={(data.overseasCountry as string) || ''}
                          onChange={(e) => onFieldChange('overseasCountry', e.target.value)}
                          className={selectClass(!!(data.overseasCountry as string))}
                        >
                          <option value="" disabled className="text-[#B6B6B6]">Select a country</option>
                          {COUNTRY_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                          ))}
                        </select>
                        {chevronIcon}
                      </div>
                    </div>
                    <div className="flex flex-col gap-sm flex-1">
                      <div className={labelClass}>
                        Was there tax paid on transfer out? <span className="text-[#E7000B]">*</span>
                      </div>
                      <div className="relative">
                        <select
                          value={(data.wasTaxPaid as string) || ''}
                          onChange={(e) => onFieldChange('wasTaxPaid', e.target.value)}
                          className={selectClass(!!(data.wasTaxPaid as string))}
                        >
                          <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
                          {YES_NO_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
                          ))}
                        </select>
                        {chevronIcon}
                      </div>
                    </div>
                  </div>

                  {/* If wasTaxPaid = Yes */}
                  {wasTaxPaid && (
                    <div className="flex flex-col gap-sm">
                      <div className={labelClass}>
                        Please provide details and backup. <span className="text-[#E7000B]">*</span>
                      </div>
                      <textarea
                        placeholder="Overseas tax paid details"
                        maxLength={2000}
                        value={(data.overseasTaxDetails as string) || ''}
                        onChange={(e) => onFieldChange('overseasTaxDetails', e.target.value)}
                        style={{ width: '888px', height: '124px' }}
                        className="rounded-lg border border-[#333] bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary px-md py-[12px] placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal resize-none"
                      />
                    </div>
                  )}
                </>
              )}

            </>
          )}

          {/* Upload Documents (optional, always visible when hasReceivedTransfers = Yes) */}
          <FileUploadField
            fieldKey="transferTaxBackupUpload"
            files={pendingFiles?.get('transferTaxBackupUpload')}
            onAddFile={onAddFile}
            onRemoveFile={onRemoveFile}
          />
        </>
      )}
    </div>
  );
}
